/* ==========================================================================
   Crumpaw · site chrome behaviour
   Mobile nav drawer + live cart count. Everything is inside this IIFE; the
   only globally visible names are the cp-* custom element tags.
   ========================================================================== */

(() => {
  'use strict';

  if (typeof window === 'undefined' || !window.customElements) return;

  /* ------------------------------------------------------------- <cp-header> */

  class CpHeader extends HTMLElement {
    connectedCallback() {
      this.burger = this.querySelector('[data-cp="burger"]');
      this.drawer = this.querySelector('[data-cp="drawer"]');
      this.closeBtn = this.querySelector('[data-cp="drawer-close"]');
      this.counts = Array.from(this.querySelectorAll('[data-cp="cart-count"]'));

      this.searchOpen = this.querySelector('[data-cp="search-open"]');
      this.searchPanel = this.querySelector('[data-cp="search-panel"]');
      this.searchInput = this.querySelector('[data-cp="search-input"]');
      this.searchClose = this.querySelector('[data-cp="search-close"]');

      this.onOpen = () => this.#toggle(true);
      this.onClose = () => this.#toggle(false);
      this.onBackdrop = (event) => {
        if (event.target === this.drawer) this.#toggle(false);
      };
      this.onKey = (event) => {
        if (event.key !== 'Escape') return;
        if (this.drawer?.hasAttribute('open')) this.#toggle(false);
        if (this.searchPanel && !this.searchPanel.hidden) this.#search(false);
      };

      // The search control is a real link to /search, so it keeps working
      // without this. Claim the click only when there is a panel to open.
      this.onSearch = (event) => {
        if (!this.searchPanel) return;
        event.preventDefault();
        this.#search(this.searchPanel.hidden);
      };
      this.onSearchClose = () => this.#search(false);
      // Horizon broadcasts this after every AJAX cart change.
      this.onCart = (event) => this.#setCount(event?.detail?.data?.itemCount);

      this.burger?.addEventListener('click', this.onOpen);
      this.closeBtn?.addEventListener('click', this.onClose);
      this.drawer?.addEventListener('click', this.onBackdrop);
      this.searchOpen?.addEventListener('click', this.onSearch);
      this.searchClose?.addEventListener('click', this.onSearchClose);
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('cart:update', this.onCart);

      // The header is cached by the section renderer, so re-read the real
      // count once on load rather than trusting the rendered value.
      this.#refresh();
    }

    disconnectedCallback() {
      this.burger?.removeEventListener('click', this.onOpen);
      this.closeBtn?.removeEventListener('click', this.onClose);
      this.drawer?.removeEventListener('click', this.onBackdrop);
      this.searchOpen?.removeEventListener('click', this.onSearch);
      this.searchClose?.removeEventListener('click', this.onSearchClose);
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('cart:update', this.onCart);
    }

    /**
     * Show or hide the search panel. Opening focuses the field, because the
     * only reason to open it is to type; closing hands focus back to the icon
     * so a keyboard user is not dropped at the top of the document.
     *
     * @param {boolean} open
     */
    #search(open) {
      if (!this.searchPanel) return;
      this.searchPanel.hidden = !open;
      this.searchOpen?.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        this.searchInput?.focus();
      } else {
        this.searchOpen?.focus();
      }
    }

    #toggle(open) {
      if (!this.drawer) return;
      if (open) {
        this.drawer.setAttribute('open', '');
        this.drawer.querySelector('a, button')?.focus();
      } else {
        this.drawer.removeAttribute('open');
        this.burger?.focus();
      }
      this.burger?.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.documentElement.style.overflow = open ? 'hidden' : '';
    }

    #setCount(value) {
      if (value == null || Number.isNaN(Number(value))) return;
      const n = Number(value);
      this.counts.forEach((node) => {
        node.textContent = String(n);
        node.hidden = n <= 0;
      });
    }

    async #refresh() {
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return;
        const cart = await res.json();
        this.#setCount(cart.item_count);
      } catch {
        /* a failed count must never break the header */
      }
    }
  }

  /* ------------------------------------------------------- <cp-sort-select> */

  class CpSortSelect extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('select');
      if (!this.select) return;
      this.onChange = () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', this.select.value);
        url.searchParams.delete('page');
        window.location.assign(url.toString());
      };
      this.select.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      this.select?.removeEventListener('change', this.onChange);
    }
  }

  /* ------------------------------------------------------------ <cp-marquee> */

  /** Duplicates the track once so the -50% keyframe loops seamlessly. */
  class CpMarquee extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('[data-cp="marquee-track"]');
      if (!track || track.dataset.cpDoubled === 'true') return;
      track.append(...Array.from(track.children).map((n) => n.cloneNode(true)));
      track.dataset.cpDoubled = 'true';
    }
  }

  /* ------------------------------------------------------- sticky offset */

  /**
   * Publish the announcement bar's height so the sticky header group can pull
   * itself up by exactly that much and leave the menu at the top of the window.
   *
   * Measured rather than hard-coded: the bar's height follows its own type and
   * padding settings, it wraps to two lines on a narrow screen, and it is not
   * there at all when the merchant turns it off. A ResizeObserver covers every
   * one of those without a breakpoint guessing at it.
   */
  const wireStickyOffset = () => {
    const group = document.getElementById('header-group');
    if (!group) return;

    const measure = () => {
      const bar = group.querySelector('.cp-announce');
      const height = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
      group.style.setProperty('--cp-announce-h', `${height}px`);
    };

    measure();

    if ('ResizeObserver' in window) {
      const bar = group.querySelector('.cp-announce');
      if (bar) new ResizeObserver(measure).observe(bar);
    } else {
      window.addEventListener('resize', measure, { passive: true });
    }

    /* The bar carries a web font, and a font swapping in after first paint
       changes its height by a pixel or two. */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure).catch(() => {});
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireStickyOffset, { once: true });
  } else {
    wireStickyOffset();
  }
  document.addEventListener('shopify:section:load', wireStickyOffset);

  const define = (tag, ctor) => {
    if (!window.customElements.get(tag)) window.customElements.define(tag, ctor);
  };

  define('cp-header', CpHeader);
  define('cp-sort-select', CpSortSelect);
  define('cp-marquee', CpMarquee);
})();
